import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { T, useTranslation } from "../context/TranslationContext";
import { ArrowLeft, Printer, Clock, Heart, Share2, Play, MessageCircle, Send, Trash2 } from "lucide-react";
import { ytEmbedUrl, ytThumb, vimeoEmbedUrl, estimateReadTime } from "../lib/media";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function paragraphs(body) {
  return (body || "").split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
}

function MediaEmbed({ video_url, audio_url, image_url }) {
  const [videoActive, setVideoActive] = useState(false);
  const yt = ytEmbedUrl(video_url);
  const vimeo = vimeoEmbedUrl(video_url);
  const embedUrl = yt || vimeo;
  if (embedUrl) {
    const thumb = yt ? ytThumb(video_url) : image_url;
    if (videoActive) {
      return (
        <div className="mt-4 aspect-video w-full border border-navy/10 overflow-hidden">
          <iframe
            src={`${embedUrl}?autoplay=1&rel=0`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            title="Embedded video"
          />
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setVideoActive(true)}
        className="mt-4 relative group w-full aspect-video bg-navy/10 overflow-hidden border border-navy/10"
        data-testid="newsletter-video-play"
      >
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-navy/80 to-navy" />
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
          <div className="w-16 h-16 rounded-full bg-saffron/90 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
            <Play size={24} className="text-white ml-1" fill="white" />
          </div>
        </div>
        <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.25em] text-white bg-black/55 px-2 py-1 backdrop-blur-sm">
          Watch teaching
        </div>
      </button>
    );
  }
  return (
    <>
      {image_url && (
        <img
          src={image_url}
          alt=""
          className="mt-4 w-full h-auto border border-navy/10"
          loading="lazy"
        />
      )}
      {audio_url && (
        <div className="mt-4 bg-navy/5 border border-navy/10 rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-saffron mb-2">Listen</div>
          <audio controls className="w-full" data-testid="newsletter-audio">
            <source src={audio_url} />
            Your browser does not support audio playback.
          </audio>
        </div>
      )}
    </>
  );
}

function ArticleBlock({ article, isLead, dropCap }) {
  const paras = paragraphs(article.body_html);
  const readMins = estimateReadTime(article.body_html);
  return (
    <article
      className={`break-inside-avoid mb-10 pb-8 border-b border-navy/15 ${
        isLead ? "lg:col-span-2" : ""
      }`}
      data-testid="newsletter-article"
    >
      {article.kicker && (
        <div className="text-[10px] uppercase tracking-[0.3em] text-saffron mb-2 inline-flex items-center gap-3">
          <T>{article.kicker}</T>
          <span className="inline-flex items-center gap-1 text-navy/45 normal-case tracking-normal">
            <Clock size={10} /> {readMins} min read
          </span>
        </div>
      )}
      <h2
        className={`font-display text-navy leading-tight ${isLead ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        <T>{article.headline}</T>
      </h2>
      {article.dek && (
        <p className="mt-2 text-base italic text-navy/75 leading-relaxed">
          <T>{article.dek}</T>
        </p>
      )}
      {article.byline && (
        <p className="mt-2 text-xs uppercase tracking-[0.25em] text-navy/55">
          <T>By</T> {article.byline}
        </p>
      )}
      <MediaEmbed
        video_url={article.video_url}
        audio_url={article.audio_url}
        image_url={article.image_url}
      />
      <div
        className="mt-4 space-y-4 text-[15px] leading-[1.75] text-navy/90"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "17px" }}
      >
        {paras.map((p, i) => (
          <p
            key={i}
            className={i === 0 && dropCap ? "broadsheet-dropcap" : ""}
          >
            <T>{p}</T>
          </p>
        ))}
      </div>
    </article>
  );
}

/** Like + share strip at the top of the issue page. */
function IssueEngagement({ issue }) {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(issue.likes || 0);
  const [shareOpen, setShareOpen] = useState(false);

  // Hydrate "liked" state from localStorage (no auth needed)
  useEffect(() => {
    try {
      const v = localStorage.getItem(`nl:liked:${issue.issue_id}`);
      if (v === "1") setLiked(true);
    } catch (e) { /* ignore */ }
  }, [issue.issue_id]);

  const toggleLike = async () => {
    const next = !liked;
    setLiked(next);
    setLikes((n) => Math.max(0, n + (next ? 1 : -1)));
    try {
      localStorage.setItem(`nl:liked:${issue.issue_id}`, next ? "1" : "0");
      await api.post(`/newsletter/issues/${issue.issue_id}/react`, { kind: next ? "like" : "unlike" });
    } catch (e) { /* still works locally even if API fails */ }
  };

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/newsletter/${issue.slug || issue.issue_id}`
    : "";
  const shareTitle = issue.title || "Ambedkarite Times — new edition";
  const shareText = `${shareTitle} · Ambedkarite Buddhist Global Network UK`;

  const share = (target) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(shareText);
    let href = "";
    if (target === "whatsapp") href = `https://wa.me/?text=${t}%20${u}`;
    else if (target === "twitter") href = `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
    else if (target === "facebook") href = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    else if (target === "email") href = `mailto:?subject=${t}&body=${encodeURIComponent(shareUrl)}`;
    if (href) window.open(href, "_blank", "noopener,noreferrer");
    setShareOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch (e) { /* ignore */ }
    setShareOpen(false);
  };

  return (
    <div className="no-print flex items-center justify-end gap-2 mb-4">
      <button
        onClick={toggleLike}
        data-testid="newsletter-like-btn"
        className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border transition-colors ${
          liked
            ? "bg-saffron/15 border-saffron/40 text-saffron"
            : "bg-white border-navy/20 text-navy/80 hover:border-saffron/40 hover:text-saffron"
        }`}
      >
        <Heart size={13} fill={liked ? "currentColor" : "none"} />
        <span>{likes > 0 ? likes : "Appreciate"}</span>
      </button>
      <div className="relative">
        <button
          onClick={() => setShareOpen((v) => !v)}
          data-testid="newsletter-share-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border border-navy/20 text-navy/80 bg-white hover:border-saffron/40 hover:text-saffron"
        >
          <Share2 size={13} /> Share
        </button>
        {shareOpen && (
          <>
            <button
              aria-label="Close share menu"
              onClick={() => setShareOpen(false)}
              className="fixed inset-0 z-30 cursor-default"
              tabIndex={-1}
            />
            <div className="absolute right-0 mt-1 w-48 bg-white border border-navy/15 rounded-sm shadow-xl z-40 py-1" data-testid="newsletter-share-menu">
              <button onClick={() => share("whatsapp")} className="block w-full text-left px-3 py-2 text-sm text-navy hover:bg-pearl" data-testid="share-whatsapp">WhatsApp</button>
              <button onClick={() => share("twitter")} className="block w-full text-left px-3 py-2 text-sm text-navy hover:bg-pearl" data-testid="share-x">X (Twitter)</button>
              <button onClick={() => share("facebook")} className="block w-full text-left px-3 py-2 text-sm text-navy hover:bg-pearl" data-testid="share-facebook">Facebook</button>
              <button onClick={() => share("email")} className="block w-full text-left px-3 py-2 text-sm text-navy hover:bg-pearl" data-testid="share-email">Email</button>
              <button onClick={copyLink} className="block w-full text-left px-3 py-2 text-sm text-navy hover:bg-pearl border-t border-navy/10" data-testid="share-copy">Copy link</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function NewsletterIssue() {
  const { slug } = useParams();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { lang } = useTranslation();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await api.get(`/newsletter/issues/${slug}`);
        if (alive) setIssue(res.data);
      } catch (err) {
        if (alive) setError(formatApiError(err.response?.data?.detail) || "Issue not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <section className="bg-pearl py-20" data-testid="newsletter-issue-loading">
        <div className="section-container text-center text-navy/60">
          <T>Loading edition…</T>
        </div>
      </section>
    );
  }

  if (error || !issue) {
    return (
      <section className="bg-pearl py-20" data-testid="newsletter-issue-error">
        <div className="section-container text-center">
          <h1 className="font-display text-3xl text-navy mb-4">
            <T>Edition not found</T>
          </h1>
          <p className="text-navy/70">{error}</p>
          <Link
            to="/newsletter"
            className="inline-block mt-6 px-5 py-2 bg-navy text-pearl text-sm hover:bg-navy-light"
          >
            <T>Back to all editions</T>
          </Link>
        </div>
      </section>
    );
  }

  const articles = issue.articles || [];
  const lead = articles[0];
  const rest = articles.slice(1);

  return (
    <section
      className="bg-[#FBF6EB] min-h-[calc(100vh-5rem)] py-12 print:py-0 print:bg-white"
      data-testid="newsletter-issue-page"
    >
      <style>{`
        .broadsheet-dropcap::first-letter {
          float: left;
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 5.5em;
          line-height: 0.85;
          padding: 8px 12px 0 0;
          color: #001B3A;
          font-weight: 600;
        }
        @media print {
          .no-print { display: none !important; }
          .broadsheet-cols { column-count: 2 !important; column-gap: 28px !important; }
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="no-print flex items-center justify-between mb-6">
          <Link
            to="/newsletter"
            className="inline-flex items-center gap-2 text-sm text-navy/70 hover:text-navy"
            data-testid="newsletter-back-link"
          >
            <ArrowLeft size={16} /> <T>All editions</T>
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="newsletter-print-btn"
            className="inline-flex items-center gap-2 text-sm text-navy/70 hover:text-saffron"
          >
            <Printer size={16} /> <T>Print this edition</T>
          </button>
        </div>

        <div className="bg-white border border-navy/10 px-6 sm:px-12 py-10 shadow-sm print:shadow-none print:border-0">
          <IssueEngagement issue={issue} />
          {/* Masthead */}
          <header className="text-center border-b-4 border-double border-navy pb-4 mb-2">
            <p className="text-[10px] uppercase tracking-[0.35em] text-saffron">
              <T>Established 2026 · Stockport · United Kingdom</T>
            </p>
            <h1
              className="mt-1 font-display text-4xl sm:text-6xl text-navy tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {issue.masthead || "The Ambedkarite Times"}
            </h1>
            <p className="text-[10px] uppercase tracking-[0.35em] text-navy/55 italic mt-1">
              <T>Educate · Agitate · Organise</T>
            </p>
          </header>
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-navy/65 border-b border-navy/30 py-2 mb-8">
            <span>{issue.edition_label}</span>
            <span className="hidden sm:inline">{lang.toUpperCase()} edition</span>
            <span>{formatDate(issue.issue_date)}</span>
          </div>

          {/* Title of the issue */}
          <div className="text-center mb-10">
            <p className="eyebrow text-saffron">
              <T>This edition</T>
            </p>
            <h2
              className="mt-2 font-display text-3xl sm:text-4xl text-navy"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              <T>{issue.title}</T>
            </h2>
            {issue.editorial_lead && (
              <p className="mt-3 max-w-3xl mx-auto italic text-navy/80 text-lg">
                <T>{issue.editorial_lead}</T>
              </p>
            )}
          </div>

          {/* Lead article, full width */}
          {lead && <ArticleBlock article={lead} isLead dropCap />}

          {/* Remaining articles in two-column broadsheet flow */}
          {rest.length > 0 && (
            <div
              className="broadsheet-cols"
              style={{
                columnCount: 2,
                columnGap: "32px",
                columnRule: "1px solid rgba(0,27,58,0.15)",
              }}
            >
              {rest.map((a, i) => (
                <ArticleBlock key={i} article={a} dropCap={i === 0} />
              ))}
            </div>
          )}

          <footer className="mt-10 pt-6 border-t-4 border-double border-navy text-center text-xs italic text-navy/65">
            <T>"Educate, Agitate, Organise" — Ambedkarite Buddhist Global Network UK</T>
          </footer>

          {/* Reactions + comments */}
          <IssueReactionsBar issue={issue} />
          <IssueComments issue={issue} />
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Typed reactions row — Like / Love / Namaste / Wisdom / Insight
// ──────────────────────────────────────────────────────────────────────────────
const REACTION_LABELS = {
  like:    { label: "Like",    glyph: "👍" },
  love:    { label: "Love",    glyph: "❤️" },
  namaste: { label: "Namaste", glyph: "🙏" },
  wisdom:  { label: "Wisdom",  glyph: "✦" },
  insight: { label: "Insight", glyph: "💡" },
};

function IssueReactionsBar({ issue }) {
  const { user } = useAuth();
  const [tally, setTally] = useState({});
  const [mine, setMine] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/newsletter/issues/${issue.issue_id}/reactions`);
      setTally(r.data.tally || {});
      setMine(r.data.my_reaction || null);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [issue.issue_id]);

  const toggle = async (rtype) => {
    if (!user) { toast.error("Login to react"); return; }
    try {
      const r = await api.post(`/newsletter/issues/${issue.issue_id}/reactions/${rtype}`);
      setTally(r.data.tally || {});
      setMine(r.data.my_reaction || null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not react");
    }
  };

  const types = Object.keys(REACTION_LABELS);
  const total = types.reduce((s, t) => s + (tally[t] || 0), 0);

  return (
    <div className="no-print mt-8 pt-5 border-t border-navy/10 flex flex-wrap items-center gap-2" data-testid="newsletter-reactions-bar">
      <span className="text-[10px] uppercase tracking-[0.25em] text-navy/55 mr-2">
        How did this edition land? · {total} {total === 1 ? "reaction" : "reactions"}
      </span>
      {types.map((t) => {
        const meta = REACTION_LABELS[t];
        const active = mine === t;
        const count = tally[t] || 0;
        return (
          <button
            key={t}
            onClick={() => toggle(t)}
            data-testid={`newsletter-reaction-${t}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-colors ${
              active
                ? "bg-saffron/15 border-saffron/50 text-saffron font-medium"
                : "bg-white border-navy/15 text-navy/75 hover:border-saffron hover:text-saffron"
            }`}
            aria-pressed={active}
          >
            <span className="text-sm">{meta.glyph}</span>
            <span>{meta.label}</span>
            {count > 0 && <span className="text-navy/60 ml-0.5">· {count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Comments
// ──────────────────────────────────────────────────────────────────────────────
function fmtRelative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 2_592_000_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}

function IssueComments({ issue }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const r = await api.get(`/newsletter/issues/${issue.issue_id}/comments`);
      setComments(r.data.comments || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [issue.issue_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/newsletter/issues/${issue.issue_id}/comments`, { body: text.trim() });
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not post");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/newsletter/comments/${cid}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
    }
  };

  const uid = user?.id || user?.user_id;

  return (
    <section className="no-print mt-8 pt-5 border-t border-navy/10" data-testid="newsletter-comments">
      <h3 className="font-display text-xl text-navy flex items-center gap-2">
        <MessageCircle size={18} className="text-saffron" /> {comments.length} {comments.length === 1 ? "comment" : "comments"}
      </h3>
      <div className="mt-4 space-y-3">
        {comments.map((c) => {
          const own = c.author?.id === uid;
          return (
            <div key={c.comment_id} className="bg-pearl border border-navy/10 rounded-lg p-3.5" data-testid={`newsletter-comment-${c.comment_id}`}>
              <div className="flex items-center gap-2 text-xs text-navy/55">
                {c.author?.membership_id ? (
                  <Link to={`/platform/profile/${c.author.membership_id}`} className="font-medium text-navy hover:text-saffron">{c.author.name}</Link>
                ) : (
                  <span className="font-medium text-navy">{c.author?.name || "Member"}</span>
                )}
                <span>· {fmtRelative(c.created_at)}</span>
                {(own || isAdmin) && (
                  <button onClick={() => remove(c.comment_id)} className="ml-auto text-red-700 hover:text-red-900" title="Delete">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="mt-1.5 text-navy/85 text-sm whitespace-pre-wrap">{c.body}</div>
            </div>
          );
        })}
      </div>
      {user ? (
        <form onSubmit={submit} className="mt-4 bg-white border border-navy/10 rounded-lg p-3.5" data-testid="newsletter-comment-form">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Add your reflection on this edition…"
            className="form-input w-full font-serif"
            data-testid="newsletter-comment-input"
          />
          <div className="flex justify-end mt-2">
            <button type="submit" disabled={busy || !text.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40" data-testid="newsletter-comment-submit">
              <Send size={13} /> {busy ? "Posting…" : "Post comment"}
            </button>
          </div>
        </form>
      ) : (
        <div className="mt-4 text-center text-sm text-navy/65 bg-pearl border border-navy/10 rounded-lg p-4">
          <Link to="/login" className="text-saffron font-medium hover:text-saffron-light">Login</Link> or <Link to="/membership" className="text-saffron font-medium hover:text-saffron-light">join free</Link> to leave a comment.
        </div>
      )}
    </section>
  );
}
